/**
 * PlanLinkActions.tsx — the staff plan-link lane: copy the emailed `#resume=`
 * handoff, or e-mail it to the patient again.
 *
 * CLINICIAN + ADMIN (CTO 2026-09-04). ONLY render after clinician approval /
 * plan invite (`planSent` / `plan_sent` / clinical approved) — before that the
 * URL is not meaningful: handover 409s and the patient has nothing to pay
 * against.
 *
 * WHY BOTH STAFF ROLES. This began ADMIN-only (portal#341). The
 * patient-recovery exercise reversed it: ~20 clinically APPROVED patients never
 * picked a plan or never paid, staff are working through them by WhatsApp and
 * phone, and whoever has the patient on the line needs the link — clinician or
 * back office. The clinician already caused this exact email at
 * Authorise→Confirm, so nothing new is exposed; and the link carries the
 * patient back to their OWN journey, so it is not a prescribing decision.
 *
 * ⚠️ NOT THE SAME CONTROL AS THE ONE IN `admin/PaymentLinkAction.tsx`, and the
 * two role rules differ ON PURPOSE — read this before "fixing" one to match the
 * other:
 *
 *   THIS (plan link, CLINICIAN+ADMIN)   `/weightloss#resume=<assessmentId>` —
 *       the patient's own journey. It carries NO money: no amount, no card, no
 *       Stripe. The patient still chooses a treatment and a term behind it.
 *   THAT (payment link, ADMIN ONLY)     a live Stripe-hosted payment page for
 *       the patient's own money. "Clinicians never see money" still holds for
 *       it — the single carve-out in this estate is a clinician's OWN
 *       compensation, and patient money is not that.
 *
 * MONEY-FREE BY CONSTRUCTION (`clinicians/clinicians.spec.tsx` sweeps the whole
 * clinician surface for `£ | pence | price | GBP`): nothing here may gain a
 * monetary field or the word "price". Say "plan", never a figure.
 *
 * ONE MOUNT ONLY (CTO 2026-09-05), and since 2026-09-07 that mount is the
 * ACTION BAND of the Patient journey panel — `pipeline/JourneyPanel.tsx`, still
 * inside the drawer's Overview tab. It briefly had three — the Overview
 * Progress row, the top of the Questionnaire tab, and the clinician's
 * recommendation-on-file banner — and the two extra ones sat on READING
 * surfaces, above the patient's own answers. Keep it single: three copies of a
 * control that emails a patient is three places for the confirm copy and the
 * gate to drift apart.
 *
 * WHAT THE MOVE INTO THE BAND CHANGED, AND WHAT IT DID NOT. The gate is the
 * same condition, lifted verbatim into `planLinkAvailable` in
 * `pipeline/journeyModel.ts` (approved / `planSent` / the `plan_sent` stage) —
 * before that the URL is not meaningful, the handover 409s and the patient has
 * nothing to pay against. This component is unchanged. What it gained is the
 * sentence above it: the band names the outstanding step, so a desk reads "the
 * patient must choose a plan and pay — no card was ever collected" and then
 * finds the control that fixes it, rather than finding a control at the bottom
 * of a panel and inferring why.
 *
 * ⚠️ ONE NEW GATE THE BAND ADDS: a CLOSED deal carries no link controls at all.
 * Emailing a plan link to a patient whose record has been closed is a contact
 * nobody can stand behind.
 *
 * RENAMED from `PlanPaymentLinkCopy` (2026-09-04). "Copy payment link" was
 * accurate while it was the only link in the portal; it stopped being accurate
 * the moment the real Stripe payment link landed on the admin failed-payments
 * board. Two adjacent controls both called "payment link" is an ops hazard —
 * a desk chasing a declined card would copy the journey link and the patient
 * would land on a plan picker instead of a card form.
 */
import { useState, type CSSProperties } from 'react';
import { ApiError } from '../api/client';
import { recordPlanLinkCopied, sendPlanHandover } from '../api/clinic';
import { formatUtcDateTime } from '../lib/format';
import { planResumeUrl } from '../lib/planResumeLink';
import { CopyLinkButton } from '../staff/ui';
/* Decorative glyphs beside the text — the labels still carry the accessible
   names (see ui/icons.tsx). */
import { IconCopyPlanLink, IconSendPlan } from '../ui/icons';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Button, Help, type AlertTone } from '../ui/kit';

/** The outcome banner: what actually happened, in words a desk can act on. */
type Notice = { tone: AlertTone; text: string };

/**
 * NO `role` PROP, ON PURPOSE. Both staff roles get exactly this control and
 * exactly this copy, so branching on role inside it would be dead weight — and
 * a role prop here would imply the component decides who may send, which it
 * must not: the callers gate the SURFACE (and clinic-service's `@Roles` gates
 * the route). The one role rule that matters is enforced structurally instead:
 * this component never has a plan body to attach, so it can never trip the
 * clinician-only 403.
 */
export function PlanLinkActions({
  assessmentId,
  lastSentAt,
  viewedAt,
  style,
}: {
  assessmentId: string;
  /**
   * When the plan link was LAST EMAILED — `dealDetail.assessment
   * .planLinkLastSentAt`, the newest of the assessment's send rows
   * (`assessment_handover_sent` / `assessment_plan_link_resent`). A COPY never
   * moves it: nothing was sent.
   *
   * ⚠️ OPTIONAL AND NULLABLE, AND BOTH FALL BACK TO THE PLACEHOLDER. Absent =
   * a clinic-service that predates the field, or a mount with no deal detail
   * behind it (ClinicianResultPanel). Null = the server looked and found no
   * send row, which means "NO RECORD of sending it", not "never sent" — every
   * re-send before 2026-09-05 was stored under the older action and journeys
   * older than the audit trail have nothing at all. Rendering either as a date
   * would put a confident wrong timestamp on a clinical record, which is the
   * exact reason the blank was shipped in the first place.
   */
  lastSentAt?: string | null;
  /**
   * `dealDetail.deal.planViewedAt` — when the patient first OPENED the link.
   *
   * Three states, and they are not interchangeable: a string is an open, NULL
   * is "no open recorded" (see the type's note — a deal predating the column,
   * or a beacon that never reached us), and ABSENT is an older server, where
   * this says nothing at all. Only rendered beside `lastSentAt`, because "no
   * open recorded" is only meaningful next to when it was sent.
   */
  viewedAt?: string | null;
  style?: CSSProperties;
}) {
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  /**
   * Re-send the plan link. NO BODY, EVER — a bare `{}` is the plain re-send,
   * and attaching `{productKey, months}` from an ADMIN session is a hard 403
   * (that is the prescribing decision, which stays clinician-side). This
   * surface never has a plan to attach anyway: the plan body belongs to
   * Authorise→Recommend in ClinicianResultPanel.
   */
  async function resend() {
    // The server cap (10 per assessment per rolling 24h) is a safety net, NOT a
    // debounce — a double click would spend two of the patient's ten sends and
    // land two identical emails. The in-flight guard is ours to own.
    if (sending) return;
    setSending(true);
    setNotice(null);
    try {
      const result = await sendPlanHandover(assessmentId);
      setConfirming(false);
      // A 201 proves the request was ACCEPTED, not that mail left the process:
      // `sent:false, stub:true` is an environment with no notifications URL.
      // Telling a desk "sent" there closes the ticket on a patient who never
      // got anything, so stub reads as a warning with the copy path attached.
      setNotice(
        result.sent
          ? { tone: 'success', text: 'Plan link re-sent — the patient has been emailed it again.' }
          : {
              tone: 'warning',
              text: 'Nothing was sent — email is not switched on in this environment. Copy the link above and send it to the patient yourself.',
            },
      );
    } catch (err) {
      setConfirming(false);
      setNotice({ tone: 'danger', text: resendFailure(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: '4px', ...style }}>
      <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap', alignItems: 'center' }}>
        <CopyLinkButton
          value={planResumeUrl(assessmentId)}
          label="Copy plan link"
          variant="ghost"
          icon={<IconCopyPlanLink />}
          /* THE AUDIT RIDES BEHIND THE COPY, NEVER IN FRONT OF IT (2026-09-06).
             `CopyLinkButton` fires this only AFTER the value reached the
             clipboard, and `recordPlanLinkCopied` returns void and swallows
             every failure — so a desk that copies a link on a flaky connection
             gets their link and no banner. A staff copy is a real contact
             attempt (the link goes out over WhatsApp or SMS and leaves no other
             trace), which is why it is worth recording at all; it is not worth
             one second of the copier's time. */
          onCopied={() => recordPlanLinkCopied(assessmentId)}
          style={{ width: 'auto' }}
        />
        <Button type="button" variant="ghost" onClick={() => setConfirming(true)} disabled={sending} style={{ width: 'auto' }}>
          {/* Dropped while sending — a glyph holding still under changing words
              reads as a stuck control. */}
          {sending ? null : <IconSendPlan />}
          {sending ? 'Re-sending…' : 'Re-send plan link'}
        </Button>
      </div>
      {/* WHEN IT WAS LAST SENT (2026-09-06) — the field this line spent a wave
          asking for. The block that used to stand here recorded that NOTHING in
          this portal could answer the question honestly, enumerated every wrong
          candidate (`DealCard.planSent` is a bare boolean; `HandoverResult` is a
          strict `{sent, stub?}` with no instant; `stageEnteredAt` is the CURRENT
          stage) and named the unblock: a `planLinkLastSentAt` on the deal-detail
          `assessment` object, derived from the send audit rows. clinic-service
          shipped exactly that, so the date is real and this is now a read.

          ⚠️ THE PLACEHOLDER DID NOT GO AWAY, IT BECAME THE FALLBACK, and that
          is the load-bearing half of this change. `lastSentAt` is absent on an
          older server and on the ClinicianResultPanel mount, and NULL when the
          server looked and found no send row — which means "no record", not
          "never sent": re-sends made before the split were written under the
          older action, and a journey older than the audit trail has nothing at
          all. A blank date would look broken and a guessed one would be worse,
          so both cases keep the original sentence. Only a real string prints a
          real instant, and it prints the TIME as well as the date — two chases
          on one day are the case a desk is actually trying to tell apart. */}
      {lastSentAt ? (
        <Help style={{ margin: 0 }}>
          Same link the patient was emailed — no expiry on the URL itself.{' '}
          {/* Built as ONE string, not `Last sent {expr}.` — a date split across
              three text nodes is a date no accessible-name or text query can
              read as a sentence, and it is a sentence staff read aloud. */}
          {`Last sent ${formatUtcDateTime(lastSentAt)}.`}
          {/* ⚠️ THE OPEN, AND THE EXACT WORDS MATTER (T-1, 2026-09-10). This is
              the sentence that tells a desk whether to chase the EMAIL or the
              PRICE, so it must not overstate what we know. "Opened" is a fact
              we hold an instant for. The absence is "No open recorded", never
              "not opened": the beacon is fire-and-forget, every deal older than
              the column is null, and the count is a floor by construction — so
              a desk reading this must be able to trust it the way they trust
              "we do not record when it was last sent" above.

              `undefined` prints NOTHING (an older server has not been asked),
              which is why this is an explicit null check and not a ternary on
              truthiness — those two cases look the same to `?:` and mean
              opposite things. */}
          {viewedAt ? ` Opened ${formatUtcDateTime(viewedAt)}.` : null}
          {viewedAt === null ? ' No open recorded.' : null}
        </Help>
      ) : (
        <Help style={{ margin: 0 }}>
          Same link the patient was emailed — no expiry on the URL itself. We do not record when it was
          last sent, so check with the patient before sending it again.
        </Help>
      )}
      {notice ? (
        // role="alert" on a failure so a desk that has already looked away is
        // told; role="status" on the quiet successes.
        <Alert tone={notice.tone} role={notice.tone === 'success' ? 'status' : 'alert'} style={{ marginBottom: 0 }}>
          {notice.text}
        </Alert>
      ) : null}
      {/* The outcome banner lives OUT here, not inside the dialog body (the
          in-dialog error pattern DealDrawer uses for its reason-gated flows),
          and the dialog closes on failure as well as success. Same reasoning
          the admin Refund flow already pins: a banner rendered while the modal
          is up sits BEHIND the overlay, so a desk sees a dismissed dialog and
          no explanation. Every outcome here is also terminal — a 429 cap, a
          missing lead email and a stub environment are all "stop clicking and
          copy the link", not "try again". */}
      {/* The estate's confirm grammar for anything that CONTACTS a patient
          (ResubscribeDialog, the photo chase): name the recipient, name what
          lands, and name what does not change. No reason field — a re-send is
          not a decision that needs auditing prose; the server already writes
          an `assessment_handover_sent` row per send with the actor's role. */}
      <ConfirmDialog
        open={confirming}
        title="Re-send the plan link?"
        confirmLabel="Re-send the link"
        busy={sending}
        onCancel={() => setConfirming(false)}
        onConfirm={resend}
        body={
          <>
            {/* Two short paragraphs, in the order the decision is made: what
                lands, then what does not change. Compressed from four sentences
                to three (2026-09-04) — a confirm nobody finishes reading is a
                confirm nobody read. Nothing was dropped: the same link, sent at
                approval, to the address on file, what they do with it, that the
                record is untouched, and the copy-link alternative are all still
                here. Those last two are the facts a re-send turns on and must
                survive any future trim. */}
            <p style={{ margin: '0 0 8px' }}>
              Emails the patient the same &ldquo;choose your plan&rdquo; link they were sent when their
              questionnaire was approved, to the address on their record &mdash; they pick a treatment and plan
              length from it.
            </p>
            <p style={{ margin: 0 }}>
              Nothing on the record changes and no new clinical decision is made. To send it yourself over
              WhatsApp or SMS, cancel and use Copy plan link.
            </p>
          </>
        }
      />
    </div>
  );
}

/**
 * Turn a handover failure into a sentence a desk can act on. The server's
 * messages are PHI-free by construction (state, config and count sentences —
 * never an address or a name), so surfacing `detail` verbatim is safe and is
 * far more useful than a generic apology: "no lead email is linked" tells the
 * desk to go and find a phone number, and the 429 cap tells them to stop
 * clicking and ring the patient instead.
 */
function resendFailure(err: unknown): string {
  if (err instanceof ApiError && err.detail) {
    // 429 (the rolling-24h cap) reads as a complete instruction on its own.
    if (err.status === 429) return err.detail;
    return `We couldn't re-send the plan link — ${err.detail}`;
  }
  return 'Sorry — we could not re-send the plan link. Copy it above and send it to the patient yourself.';
}
